import React, { useEffect, useState, Fragment } from "react";
import AdminTable from "../utlis/AdminTable";
import Label from "src/components/Label";
import { IconButton, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Loader from "src/components/loader/Loader";
import MoreMenu from "../utlis/MoreMenu";
import ContentDialog from "./ContentDialog";
import { useDispatch } from "react-redux";
import { showAlert } from "src/redux/slices/AlertSlice";
import { post, get } from "src/services/content.service";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { fDateTime } from "../../../../utils/formatTime";
import ConfirmDialog from "../Popup/ConfirmDialog";
import { useTranslation } from 'react-i18next';

const AdminContent = () => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [contents, setContents] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selected, setSelected] = useState([]);
    const [slug, setSlug] = useState(null);
    const [contentType, setContentType] = useState(null);
    const [submitStatus, setSubmitStatus] = useState();
    const [deleteIds, setDeleteIds] = useState(null);
    const { t } = useTranslation();
    const typeLabels = {
        content_list: t("AddNodeDialog.NavTypes.content_list"),
        nested_list: t("AddNodeDialog.NavTypes.nested_list"),
        tab: t("AddNodeDialog.NavTypes.tab"),
        list: t("AddNodeDialog.NavTypes.list"),
        content: t("AddNodeDialog.NavTypes.content"),
    };

    const TABLE_HEAD = [
        { id: "Id", label: t("AdminContent.AdminTable.headers.Id"), alignRight: false, search: false },
        { id: "Title", label: t("AdminContent.AdminTable.headers.Title"), alignRight: false, search: true },
        { id: "Type", label: t("AdminContent.AdminTable.headers.Type"), alignRight: false, search: true },
        { id: "Status", label: t("AdminContent.AdminTable.headers.Status"), alignRight: false },
        { id: "Created", label: t("AdminContent.AdminTable.headers.Created"), alignRight: false },
        { id: "Author", label: t("AdminContent.AdminTable.headers.Author"), alignRight: false },
        { id: "More", alignRight: true },
    ];

    const dispatch = useDispatch();

    const clearDeletedRows = (ids) => {
        let tempContents = [...contents];

        for (const id of ids) {
            const tmpRowIdx = contents.findIndex(
                (content) => content.id === id
            );
            if (tmpRowIdx !== -1) {
                tempContents.splice(tmpRowIdx, 1);
            } else {
                console.log("Content idx not found");
            }
        }

        setContents(tempContents);
        setSelected([]);
    };

    const deleteContent = async (ids) => {
        const body = JSON.stringify({ ids });
        try {
            setLoading(true);
            await post(dispatch, "/api/content/admin/delete/", body);
            clearDeletedRows(ids);
        } catch (err) {
            dispatch(
                showAlert({
                    message: err,
                    severity: "error",
                })
            );
        } finally {
            setLoading(false);
        }
    };

    const editContent = (content) => {
        setSlug(content.slug);
        setContentType(content.type);
        setShowModal(true);
    };

    const openDeleteConfirm = (ids) => {
        if (!ids || ids.length === 0) {
            return;
        }
        setDeleteIds(ids);
    };

    const closeDeleteConfirm = () => {
        setDeleteIds(null);
    };

    const submitDeleteConfirm = async () => {
        const ids = deleteIds;
        setDeleteIds(null);
        if (!ids || ids.length === 0) {
            return;
        }
        await deleteContent(ids);
    };

    useEffect(() => {
        if (contents && contents.length) {
            setRows(
                contents.map((row) => ({
                    Slug: row.slug,
                    Id: row.id,
                    Title: row.title,
                    Body: row.body,
                    Type: typeLabels[row.type] || row.type,
                    Status: (
                        <Label
                            variant="ghost"
                            color={row.status ? "success" : "error"}
                        >
                            {row.status ? t("AdminContent.AdminTable.status.active") : t("AdminContent.AdminTable.status.inactive")}
                        </Label>
                    ),
                    Created: fDateTime(row.created),
                    Author: row.uid.name,
                    More: (
                        <MoreMenu
                            options={[
                                {
                                    label: t("buttonCommons.edit"),
                                    icon: <EditOutlinedIcon />,
                                    handler: () => {
                                        editContent(row);
                                    },
                                },
                                {
                                    label: t("buttonCommons.delete"),
                                    icon: <DeleteOutlineOutlinedIcon />,
                                    handler: () => {
                                        openDeleteConfirm([row.id]);
                                    },
                                },
                            ]}
                        />
                    ),
                }))
            );
        }
    }, [contents, t]);

    useEffect(() => {
        if (submitStatus !== null && submitStatus !== undefined) {
            let message, severity;
            if (submitStatus) {
                message = t("AdminContent.update.success");
                severity = "success";
            } else {
                message = t("AdminContent.update.error");
                severity = "error";
            }
            dispatch(
                showAlert({
                    message: message,
                    severity: severity,
                })
            );
        }
        return () => {
            setSubmitStatus(null);
        };
    }, [submitStatus, dispatch]);

    useEffect(() => {
        window.scrollTo(0, 0);
        const fetchData = async () => {
            setLoading(true);
            try {
                const res = await get(dispatch, `/api/content/admin/all/`);
                setContents(res.data);
            } catch (err) {
                dispatch(
                    showAlert({
                        message: err,
                        severity: "error",
                    })
                );
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dispatch]);

    const handleDeleteSelected = () => {
        openDeleteConfirm(selected);
    };

    return (
        <Fragment>
            {loading ? (
                <Loader />
            ) : (
                <Fragment>
                    <ContentDialog
                        type="edit"
                        open={showModal}
                        onClose={() => {
                            setShowModal(false);
                        }}
                        submitStatus={setSubmitStatus}
                        slug={slug}
                        contentType={contentType}
                    />
                    <AdminTable
                        searchPlaceholder={t("AdminTableCommons.searchPlaceholder")}
                        headers={TABLE_HEAD}
                        rows={rows}
                        selection={{
                            selected: selected,
                            setSelected: setSelected,
                        }}
                        showChildrenWhenSelected={true}
                        contextMenuOptions={[
                            {
                                label: t("buttonCommons.edit"),
                                icon: <EditOutlinedIcon />,
                                handler: (id) => {
                                    editContent(contents[id]);
                                },
                            },
                            {
                                label: t("buttonCommons.delete"),
                                icon: <DeleteOutlineOutlinedIcon />,
                                handler: (id) => {
                                    openDeleteConfirm([contents[id].id]);
                                },
                            },
                        ]}
                    >
                        <Tooltip title={t("buttonCommons.delete")}>
                            <IconButton
                                color="primary"
                                aria-label={t("AdminTableCommons.deleteSelected")}
                                component="span"
                                onClick={handleDeleteSelected}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </AdminTable>
                    <ConfirmDialog
                        open={Boolean(deleteIds)}
                        onClose={closeDeleteConfirm}
                        onSubmit={submitDeleteConfirm}
                        title={t("ConfirmDialog.deleteTitle", {
                            defaultValue: "Confirm deletion",
                        })}
                        text={t("ConfirmDialog.deleteContentText", {
                            defaultValue:
                                "Do you really want to delete this content? This action cannot be undone.",
                        })}
                        confirmLabel={t("buttonCommons.delete")}
                        confirmColor="error"
                    />
                </Fragment>
            )}
        </Fragment>
    );
};

export default AdminContent;
